import { type RefObject, useState } from "react"

import { useCountUp } from "@/hooks/useCountUp"

type AboutTab = {
  label: string
  stats: Array<{ value: number; suffix: string; label: string }>
  progressBars: Array<{ label: string; pct: number }>
  title: string
  paragraphs: [string, string]
  images: Array<{ src: string; alt: string }>
}

const aboutTabs: AboutTab[] = [
  {
    label: "Interior Design",
    stats: [
      { value: 100, suffix: "+", label: "Projects" },
      { value: 80, suffix: "+", label: "Clients" },
      { value: 15, suffix: "+", label: "Years Experience" },
      { value: 1, suffix: "", label: "Location" },
    ],
    progressBars: [
      { label: "Interior Design", pct: 87 },
      { label: "Carpentry", pct: 78 },
      { label: "Renovation", pct: 70 },
    ],
    title: "OUR MISSION STATEMENT",
    paragraphs: [
      "Design Inside by Vicky creates personalized, functional interiors combining creativity and craftsmanship for your dream space. With over 15 years of experience, we have transformed hundreds of homes across Hyderabad into beautiful, functional living environments.",
      "Our commitment to quality and attention to detail sets us apart. From initial consultation to final execution, we ensure every project exceeds expectations.",
    ],
    images: [
      {
        src: "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?w=400&q=80",
        alt: "Interior workspace",
      },
      {
        src: "https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=400&q=80",
        alt: "Modern room design",
      },
    ],
  },
  {
    label: "Renovation",
    stats: [
      { value: 60, suffix: "+", label: "Homes Upgraded" },
      { value: 25, suffix: "+", label: "Site Teams" },
      { value: 12, suffix: "+", label: "Service Areas" },
      { value: 98, suffix: "%", label: "On-Time Delivery" },
    ],
    progressBars: [
      { label: "Civil Works", pct: 84 },
      { label: "Painting", pct: 76 },
      { label: "Flooring", pct: 72 },
    ],
    title: "RENOVATION EXPERTISE",
    paragraphs: [
      "We modernize existing homes with practical upgrades that improve flow, comfort, and day-to-day living. Each renovation plan is tailored around the current structure and your future needs.",
      "From wall finishes to flooring, storage, and lighting improvements, our team coordinates every stage carefully so the finished space feels intentional, cohesive, and ready to use.",
    ],
    images: [
      {
        src: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=400&q=80",
        alt: "Renovated kitchen interior",
      },
      {
        src: "https://images.unsplash.com/photo-1600566753376-12c8ab7fb75b?w=400&q=80",
        alt: "Renovated ceiling and lighting work",
      },
    ],
  },
  {
    label: "Fixtures",
    stats: [
      { value: 45, suffix: "+", label: "Fixture Packages" },
      { value: 30, suffix: "+", label: "Custom Solutions" },
      { value: 20, suffix: "+", label: "Trusted Vendors" },
      { value: 100, suffix: "%", label: "Installation Support" },
    ],
    progressBars: [
      { label: "Lighting Solutions", pct: 82 },
      { label: "Glass & Aluminum", pct: 74 },
      { label: "Curtains & Blinds", pct: 69 },
    ],
    title: "FIXTURES AND FINISHES",
    paragraphs: [
      "The right fixtures define how a space feels and functions. We help select lighting, fittings, windows, glass work, and finishing details that align with the overall design language of your home.",
      "Every specification is chosen for durability, visual balance, and ease of maintenance so the final result is not only attractive but also dependable over time.",
    ],
    images: [
      {
        src: "https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=400&q=80",
        alt: "Bathroom fixtures and finishes",
      },
      {
        src: "https://images.unsplash.com/photo-1600573472591-ee6b68d14c68?w=400&q=80",
        alt: "Interior fixture installation",
      },
    ],
  },
]

function StatItem({
  value,
  suffix,
  label,
}: {
  value: number
  suffix: string
  label: string
}) {
  const { count, ref } = useCountUp(value)

  return (
    <div ref={ref as RefObject<HTMLDivElement>} className="text-center">
      <span className="font-heading text-3xl text-navy">
        {count}
        {suffix}
      </span>
      <p className="mt-1 text-[11px] font-light tracking-[0.1em] text-gray-400 uppercase">
        {label}
      </p>
    </div>
  )
}

export function About() {
  const [activeTab, setActiveTab] = useState(0)
  const activeContent = aboutTabs[activeTab]

  return (
    <section id="about" className="py-20">
      <div className="mx-auto max-w-[1400px] px-8">
        <div className="grid gap-16 md:grid-cols-2">
          <div className="reveal">
            <h2 className="font-heading text-[40px] leading-[1.15] text-navy sm:text-[48px]">
              OUR SERVICES
              <br />
              DESIGN INSIDE
            </h2>

            <div className="mt-8 flex gap-0 border-b border-gray-200" role="tablist">
              {aboutTabs.map((tab, index) => (
                <button
                  key={tab.label}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === index}
                  onClick={() => setActiveTab(index)}
                  className={`flex-1 border-b-2 pb-3 text-[13px] font-light tracking-[0.1em] uppercase transition-all ${
                    activeTab === index
                      ? "border-navy text-navy"
                      : "border-transparent text-gray-400 hover:border-navy hover:text-navy"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div key={activeContent.label} className="mt-12 grid grid-cols-4 gap-4">
              {activeContent.stats.map((stat) => (
                <StatItem
                  key={`${activeContent.label}-${stat.label}`}
                  value={stat.value}
                  suffix={stat.suffix}
                  label={stat.label}
                />
              ))}
            </div>

            <div key={`${activeContent.label}-progress`} className="mt-12 space-y-5">
              {activeContent.progressBars.map((bar) => (
                <div key={bar.label}>
                  <div className="mb-2 flex justify-between">
                    <span className="text-sm font-light text-navy">{bar.label}</span>
                    <span className="text-sm font-light text-gray-400">{bar.pct}%</span>
                  </div>
                  <div className="h-[3px] w-full bg-gray-100">
                    <div
                      className="progress-fill h-full bg-navy"
                      style={{ width: `${bar.pct}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="reveal" style={{ transitionDelay: "150ms" }}>
            <h3 className="font-heading text-2xl text-navy">{activeContent.title}</h3>
            <p className="mt-4 text-[15px] leading-relaxed font-light text-gray-500">
              {activeContent.paragraphs[0]}
            </p>
            <p className="mt-4 text-[15px] leading-relaxed font-light text-gray-500">
              {activeContent.paragraphs[1]}
            </p>

            <div className="mt-8 grid grid-cols-2 gap-4">
              {activeContent.images.map((image) => (
                <div key={image.src} className="overflow-hidden bg-gray-100">
                  <img
                    src={image.src}
                    alt={image.alt}
                    className="h-48 w-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
